import { evaluate } from "../services/ruleEngine";
import { loadPolicy } from "../services/policyService";
import { buildMessage, Lang, langFromDisplayLanguage } from "../services/messageService";
import { setWarningHeader, removeWarningHeader } from "../services/headerService";
import { getLabelNames } from "../services/labelService";
import { getContentText } from "../services/contentService";

const WATCHDOG_MS = 4000;

function getRecipients(field: Office.Recipients): Promise<Office.EmailAddressDetails[]> {
  return new Promise((resolve) => {
    field.getAsync((result) => {
      resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value : []);
    });
  });
}

function detectLang(): Lang {
  return langFromDisplayLanguage(Office.context.displayLanguage);
}

// Shows a compose-window info bar so handler progress is diagnosable without devtools
// (event-based Smart Alerts handlers run in a hidden runtime, not an inspectable page frame).
function showDebugNotification(item: Office.MessageCompose, message: string): void {
  item.notificationMessages.replaceAsync("kmex-debug", {
    type: Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage,
    message: `KMEX: ${message}`,
    icon: "icon-16",
    persistent: false,
  });
}

async function onMessageSendHandler(event: Office.MailboxEvent): Promise<void> {
  console.log("[KMEX] onMessageSendHandler invoked");
  let stage = "init";
  let completed = false;
  const complete = (options: Office.SmartAlertsEventCompletedOptions) => {
    if (completed) return;
    completed = true;
    event.completed(options);
  };

  const item = Office.context.mailbox.item as Office.MessageCompose;

  // Proves the handler was actually invoked, independent of the watchdog/decision logic below.
  showDebugNotification(item, "handler invoked");

  const watchdog = new Promise<void>((resolve) => {
    setTimeout(() => {
      console.error(`[KMEX] watchdog fired at stage: ${stage}`);
      showDebugNotification(item, `watchdog fired at stage: ${stage}`);
      complete({ allowEvent: true }); // fail open rather than hang forever
      resolve();
    }, WATCHDOG_MS);
  });

  const run = async () => {
    try {
      stage = "getRecipients";
      const [to, cc, bcc] = await Promise.all([
        getRecipients(item.to),
        getRecipients(item.cc),
        getRecipients(item.bcc),
      ]);

      stage = "loadPolicy";
      const policy = await loadPolicy();
      const addresses = [...to, ...cc, ...bcc].map((r) => r.emailAddress);

      stage = "evaluate";
      const [labelNames, text] = await Promise.all([
        policy.labelRule.enabled ? getLabelNames(item) : Promise.resolve(null),
        policy.contentRule.enabled ? getContentText(item, policy.contentRule.maxBodyChars) : Promise.resolve(""),
      ]);
      const toCcAddresses = [...to, ...cc].map((r) => r.emailAddress);
      const decision = evaluate(addresses, policy, labelNames, { text, toCcAddresses });

      if (
        decision.level === "blockedLabel" ||
        decision.level === "blockedUnlabeled" ||
        decision.level === "blockedCustomerLabel"
      ) {
        // the manifest SendMode is SoftBlock, so omitting sendModeOverride removes "Send anyway"
        stage = "completeBlocked";
        const blocked = buildMessage(decision, policy, detectLang());
        const blockOptions: Office.SmartAlertsEventCompletedOptions = {
          allowEvent: false,
          errorMessage: blocked.plain,
        };
        if (Office.context.requirements.isSetSupported("Mailbox", "1.15")) {
          blockOptions.errorMessageMarkdown = blocked.markdown;
        }
        complete(blockOptions);
        return;
      }

      if (decision.level === "none") {
        stage = "removeWarningHeader";
        await removeWarningHeader(item); // FR-18: re-evaluation that no longer warns clears the header
        complete({ allowEvent: true });
        return;
      }

      stage = "setWarningHeader";
      await setWarningHeader(item, decision);

      stage = "buildMessage";
      const lang = detectLang();
      const message = buildMessage(decision, policy, lang);

      const options: Office.SmartAlertsEventCompletedOptions = {
        allowEvent: false,
        errorMessage: message.plain,
      };

      if (Office.context.requirements.isSetSupported("Mailbox", "1.14")) {
        options.cancelLabel = message.cancelLabel;
        options.sendModeOverride = Office.MailboxEnums.SendModeOverride.PromptUser;
      }
      if (Office.context.requirements.isSetSupported("Mailbox", "1.15")) {
        options.errorMessageMarkdown = message.markdown;
      }

      stage = "complete";
      complete(options);
    } catch (err) {
      console.error(`[KMEX] error at stage ${stage}:`, err);
      complete({ allowEvent: true }); // FR-19: fail open on any unexpected error
    }
  };

  await Promise.race([run(), watchdog]);
}


console.log("[KMEX] bundle loaded, associating handler");
Office.actions.associate("onMessageSendHandler", onMessageSendHandler);
console.log("[KMEX] handler associated");

let officeReady = false;
Office.onReady((info) => {
  officeReady = true;
  console.log(`[KMEX] Office.onReady host=${info.host} platform=${info.platform} mailbox=${!!Office.context?.mailbox}`);
});
setTimeout(() => {
  if (!officeReady) console.error("[KMEX] Office.onReady NOT fired after 5s - host handshake failed");
}, 5000);
